<template>
    <div class="Program">
        <div class="container-fluid">
            <div class="row">
                <div class="col col-md-4">
                    <div class="record-section">
                        <h4>Enrollment Information:</h4>
                        <p>
                            <span class="bold">Program Name: </span> {{ ChildRecordData.SubProgramName }}

                        </p>
                        <p>
                            <span class="bold">Start Date: </span>
                            <DateEditor v-bind:old-value="ChildRecordData.ClientProgramMembership.StartDate"
                                        v-bind:record-id="ChildRecordData.ClientProgramMembership.Id"
                                        v-bind:edit-mode="EditMode"
                                        data-label="Start Date"
                                        property-name="StartDate"
                                        update-method="Residential/Clients/UpdateStartDate"
                                        required="true"
                                        warning-notice="Modifying the start date (original placement date) may affect other records, such as initial home placement dates, initial program assignment dates, etc...  You have been warned."
                                        :global-update="true"
                                        edit-permission="Residential/Clients/UpdateStartDate" />
                        </p>
                        <p>
                            <span class="bold">Mental Health Open Date: </span>
                            <DateEditor v-bind:old-value="ChildRecordData.ClientProgramMembership.MHStartDate"
                                        v-bind:record-id="ChildRecordData.ClientProgramMembership.Id"
                                        v-bind:edit-mode="EditMode"
                                        data-label="Mental Health Open Date"
                                        property-name="MHStartDate"
                                        update-method="Foster/FosterClients/UpdateClientSubProgramMembership"
                                        :required="false"
                                        :global-update="true"
                                        edit-permission="MentalHealth.CreateClinicalDocuments" />
                        </p>
                        <p>
                            <span class="bold">First Clinical Contact: </span>
                            <DateEditor v-bind:old-value="ChildRecordData.ClientProgramMembership.FirstClinicalContactDate"
                                        v-bind:record-id="ChildRecordData.ClientProgramMembership.Id"
                                        v-bind:edit-mode="EditMode"
                                        data-label="First Clinical Contact"
                                        property-name="FirstClinicalContactDate"
                                        update-method="Foster/FosterClients/UpdateClientSubProgramMembership"
                                        :required="false"
                                        :global-update="true"
                                        edit-permission="MentalHealth.CreateClinicalDocuments" />
                        </p>
                        <p>
                            <span class="bold">Mental Closure Date: </span>
                            <DateEditor v-bind:old-value="ChildRecordData.ClientProgramMembership.MHEndDate"
                                        v-bind:record-id="ChildRecordData.ClientProgramMembership.Id"
                                        v-bind:edit-mode="EditMode"
                                        data-label="Mental Health Closure Date"
                                        property-name="MHEndDate"
                                        update-method="Foster/FosterClients/UpdateClientSubProgramMembership"
                                        :required="false"
                                        :global-update="true"
                                        edit-permission="MentalHealth.CreateClinicalDocuments" />
                        </p>
                        <p>
                            <span class="bold">Termination Info: </span>
                            <DischargeEditor v-bind:old-end-date="ChildRecordData.ClientProgramMembership.EndDate"
                                             v-bind:old-reason="ChildRecordData.ClientProgramMembership.SubProgramDischargeReason"
                                             v-bind:record-id="ChildRecordData.ClientProgramMembership.Id"
                                             v-bind:edit-mode="EditMode"
                                             data-label="Termination Info"
                                             discharge-method="Residential/Clients/TerminateClient"
                                             revert-method="Residential/Clients/RevertTermination"
                                             v-bind:options="GetSubProgramTerminationReasons"
                                             discharge-language="Terminate" />
                        </p>
                        <p>
                            <span class="bold">Placing Agency: </span>
                            <SelectEditor v-bind:old-value="ChildRecordData.ClientProgramMembership.PlacementAgency.AgencyTypeID"
                                          v-bind:old-value-text="ChildRecordData.ClientProgramMembership.PlacementAgency.Name"
                                          v-bind:record-id="ChildRecordData.ClientProgramMembership.Id"
                                          v-bind:edit-mode="EditMode"
                                          data-label="Placing Agency"
                                          property-name="AgencyID"
                                          update-method="Residential/Clients/UpdateClientSubProgramMembership"
                                          v-bind:options="Agencies" />
                        </p>
                        <p>
                            <span class="bold">Welligent Program Round: </span>
                            <IntegerEditor v-bind:old-value="ChildRecordData.ClientProgramMembership.WelligentProgramRound"
                                           v-bind:record-id="ChildRecordData.ClientProgramMembership.Id"
                                           v-bind:edit-mode="EditMode"
                                           data-label="WelligentID"
                                           property-name="WelligentProgramRound"
                                           update-method="Residential/Clients/UpdateWelligentID"
                                           :global-update="true" />
                        </p>
                        <p>
                            <span class="bold">Episode Number: </span>
                            <StringEditor v-bind:old-value="ChildRecordData.ClientProgramMembership.EpisodeNumber"
                                          v-bind:record-id="ChildRecordData.ClientProgramMembership.Id"
                                          v-bind:edit-mode="EditMode"
                                          data-label="Episode Number"
                                          property-name="EpisodeNumber"
                                          update-method="Residential/Clients/UpdateClientSubProgramMembership"
                                          :required="false"
                                          :chart-id="ChildRecordData.Id" />
                        </p>
                        <p>
                            <span class="bold">Primary Language Spoken: </span>
                            <SelectEditor v-bind:old-value="ChildRecordData.ClientProgramMembership.PrimaryLanguageSpoken"
                                          v-bind:old-value-text="ChildRecordData.ClientProgramMembership.PrimaryLanguageSpoken"
                                          v-bind:record-id="ChildRecordData.ClientProgramMembership.Id"
                                          v-bind:edit-mode="EditMode"
                                          data-label="Primary Language Spoken"
                                          property-name="PrimaryLanguageSpoken"
                                          update-method="Residential/Clients/UpdateClientSubProgramMembership"
                                          v-bind:options="PrimaryLanguageSpokenOptions" />
                        </p>
                        <p>
                            <span class="bold">Days in Program: </span><span>{{ ChildRecordData.DaysInProgram }}</span>
                        </p>
                    </div>
                </div>
                <div class="col col-md-4">
                    <div class="record-section">
                        <h4>Allergies:</h4>
                        <TextAreaEditor v-bind:old-value="ChildRecordData.ClientProgramMembership.Allergies"
                                        v-bind:record-id="ChildRecordData.ClientProgramMembership.Id"
                                        v-bind:edit-mode="EditMode"
                                        data-label="Allergies"
                                        property-name="Allergies"
                                        update-method="Residential/Clients/UpdateAllergies"
                                        :required="false"
                                        :global-update="true" />
                    </div>
                </div>
                <div class="col col-md-4">
                    <div class="record-section">
                        <h4>Running Summary:</h4>
                        <TextAreaEditor v-bind:old-value="ChildRecordData.ClientProgramMembership.RunningSummary"
                                        v-bind:record-id="ChildRecordData.ClientProgramMembership.Id"
                                        v-bind:edit-mode="EditMode"
                                        data-label="Running Summary"
                                        property-name="RunningSummary"
                                        update-method="Residential/Clients/UpdateRunningSummary"
                                        :required="false"
                                        :global-update="true" />
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col col-md-6">
                    <div class="record-section-row">
                        <h4>Service Enrollment:</h4>
                        <SingleMembershipEditor :fields="[{name: 'ProgramService.Name', title: 'SERVICE'}, {name: 'StartDate', title: 'STARTED', callback: 'formatDate|MM/DD/YY'}, {name: 'EndDate', title: 'ENDED', callback: 'formatDate|MM/DD/YY|N/A'}]"
                                                :field-data="ChildRecordData.ClientProgramMembership.ProgramServices.filter(ps => ps.ProgramService.ServiceType == 'Census')"
                                                :edit-mode="EditMode"
                                                select-label="Program Service:"
                                                options-type="API"
                                                options-method="Core/ProgramService/GetProgramServicesForSubProgram"
                                                :options-method-record-id="ChildRecordData.ClientProgramMembership.SubProgramID"
                                                add-record-method="Core/ProgramService/AssignProgramServiceToClientSubProgramMembership"
                                                add-record-record-property-name="ProgramServiceID"
                                                :add-record-additional-payload="{ClientSubProgramMembershipID: ChildRecordData.ClientProgramMembership.Id}"
                                                add-record-modal-title="Enroll Child In Initial Program Service"
                                                transfer-record-method="Core/ProgramService/TransferProgramServiceToClientSubProgramMembership"
                                                transfer-record-record-property-name="ProgramServiceID"
                                                :transfer-record-additional-payload="{ClientSubProgramMembershipID: ChildRecordData.ClientProgramMembership.Id}"
                                                transfer-record-modal-title="Transfer Program Service"
                                                transfer-record-effective-date-property-name="StartDate"
                                                delete-record-method="Core/ProgramService/DeleteProgramServiceToClientSubProgramMembership"
                                                :delete-record-additional-payload="{Id: ChildRecordData.ClientProgramMembership.Id}"
                                                delete-record-modal-title="Delete Current Program Service"
                                                delete-record-modal-text="NOTICE: You are about to delete this client's current Program Service.  If there was a previous service assigned, that service will be re-activated." />
                    </div>
                </div>
                <div class="col col-md-6">
                    <div class="record-section-row">
                        <HousingAssignments v-bind:child-record-data="ChildRecordData"
                                            Title="House Assignments"
                                            add-method="Residential/ClientResidence/AssignResidenceToClientSubProgramMembership"
                                            options-method="Residential/ClientResidence/GetResidencesForSubProgram"
                                            transfer-method="Residential/ClientResidence/TransferResidenceToClientSubProgramMembership"
                                            delete-method="Residential/ClientResidence/DeleteResidenceToClientSubProgramMembership"
                                            :field-data="ChildRecordData.ClientProgramMembership.ResidenceMemberships"
                                            :edit-mode="EditMode" />
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col">
                    <div class="record-section-row">
                        <h4>Acuity:</h4>
                        <Acuity :chart-id="ChildRecordData.Id" :edit-mode="EditMode" />
                    </div>
                </div>
            </div>
            <div class="row" v-if="CheckPermission('ProblemsListAccess')">
                <div class="col">
                    <div class="record-section-row">
                        <ProblemsListEditor :chart-id="ChildRecordData.Id" :edit-mode="EditMode && CheckPermission('ProblemsListEdit')" />
                    </div>
                </div>
            </div>
        </div>
        <EditMode :EditMode="EditMode" @update-edit-mode="UpdateEditMode" />
    </div>
</template>

<script>
    //import icons
    import { library } from '@fortawesome/fontawesome-svg-core';
    import { faEdit } from '@fortawesome/free-solid-svg-icons';
    library.add(faEdit);
    //import components
    import DateEditor from '../../../Core/ItemEditors/DateEditor.vue';
    import IntegerEditor from '../../../Core/ItemEditors/IntegerEditor.vue';
    import StringEditor from '../../../Core/ItemEditors/StringEditor.vue';
    import SelectEditor from '../../../Core/ItemEditors/SelectEditor.vue';
    import TextAreaEditor from '../../../Core/ItemEditors/TextAreaEditor.vue';
    import DischargeEditor from '../../../Core/ItemEditors/DischargeEditor.vue';
    import SingleMembershipEditor from '../../../Core/ItemEditors/SingleMembershipEditor.vue';
    import EditMode from '../../../Core/ItemEditors/EditMode.vue';
    import Acuity from './Acuity.vue'
    import ProblemsListEditor from '../../../MentalHealth/Editors/ProblemsListEditor.vue';
    import HousingAssignments from "../../../Core/ItemEditors/HousingAssignments.vue";
    export default {
        props: [
            'ChildRecordData'
        ],
        data: function () {
            return {
                Agencies: [],
                EditMode: false
            };
        },
        methods: {
            formatDate(value, fmt = 'MM/DD/YY', defaultText = '') {
                return (value == null)
                    ? defaultText
                    : moment(value, 'YYYY-MM-DD').format(fmt)
            },
            CheckPermission(permissionName) {
                return this.$store.getters["User.PrivateProfile.CheckPermission"](permissionName);
            },
            UpdateEditMode() {
                this.EditMode = !this.EditMode;
            },
            GetAgencies() {
                this.$MasterAPI.ExecuteRequest("GET", "Residential/Clients/GetAgencies", {}, this.GetAgenciesSuccess, () => { }, false);
            },
            GetAgenciesSuccess(response) {
                this.Agencies = response.data.data;
                for (var i = 0; i < this.Agencies.length; i++) {
                    this.Agencies[i].text = this.Agencies[i].Name;
                    this.Agencies[i].value = this.Agencies[i].Id;
                }
            }
        },
        components: {
            DateEditor,
            StringEditor,
            DischargeEditor,
            SingleMembershipEditor,
            TextAreaEditor,
            EditMode,
            IntegerEditor,
            Acuity,
            ProblemsListEditor,
            SelectEditor,
            HousingAssignments
        },
        mounted() {
            this.GetAgencies();
        },
        computed: {
            GetSubProgramTerminationReasons: function () {
                let subprogram = this.$store.getters["User.PrivateProfile.SubPrograms.GetSubProgram"](this.ChildRecordData.ClientProgramMembership.SubProgramID);
                let returnObj = [];
                if (subprogram.DischargeReasons.length > 0) {
                    subprogram.DischargeReasons.forEach(function (item) {
                        returnObj.push({ text: item.Label, value: item.Id, grouping: item.Grouping });
                    })
                }
                return returnObj;
            },
            PrimaryLanguageSpokenOptions: function () {
                return this.$store.state.Options.PrimaryLanguageSpoken;
            },
        }
    }
</script>

<style scoped>
</style>